import { GeoJSON } from 'leaflet';
import { computed, Signal } from '@angular/core';
import {
  FeatureCollection,
  GeoData,
  KeyValueDistricts,
} from '../../models/geo-data';
import { makeUniqueStringArray } from '../../utility/array.utility';

/**
 * Filters the given features object to only include districts that match the specified search word.
 * If the search word is an empty string or missing, all districts are included in the result.
 *
 * @param {KeyValueDistricts} features - An object containing districts as key-value pairs, where the key is the district identifier and the value is the district details.
 * @param {string} searchWord - The search string used to filter districts. Matching is performed case-insensitively against the district's name or key.
 * @returns {KeyValueDistricts} A filtered object containing only the districts that match the search word.
 */
export const filterFeaturesBySearchWord = (
  features: KeyValueDistricts,
  searchWord: string
): KeyValueDistricts => {
  let filteredDistricts: KeyValueDistricts = {};
  if (!searchWord) {
    filteredDistricts = features;
  } else {
    for (const key in features) {
      const district = features[key];
      if (
        district.name.toLowerCase().indexOf(searchWord.toLowerCase()) > -1 ||
        district.key.toLowerCase().indexOf(searchWord.toLowerCase()) > -1
      ) {
        filteredDistricts[district.key] = district;
      }
    }
  }
  return filteredDistricts;
};

/**
 * Retrieves a computed feature collection from a geo-data selection.
 *
 * This function processes geographic data and returns a GeoJSON object based on the provided input.
 * If a grouping key is provided, it filters the GeoJSON features by the specified group key.
 * If the data lacks geometries or is invalid, it returns null.
 *
 * @param {() => GeoData | null} geoData - A function that returns geographic data or null.
 * @param {string} [groupKey] - An optional key used to filter features in the GeoJSON.
 * @returns {Signal<GeoJSON | null>} A Signal to the filtered GeoJSON object, or null if invalid or empty.
 */
export const getFeatureCollectionFromSelection = (
  geoData: () => GeoData | null,
  groupKey?: string
): Signal<any | null> => {
  return computed(() => {
    const data = geoData();
    if (data && data.geoJson) {
      const geoJSON = JSON.parse(data.geoJson);
      if (!geoJsonHasGeometries(geoJSON)) return null;
      if (groupKey) {
        return filterGeoJsonFeaturesByGroup(geoJSON, groupKey);
      } else {
        return geoJSON;
      }
    } else {
      return null;
    }
  });
};

/**
 * Checks if a given GeoJSON object contains at least one feature with geometries.
 *
 * @param {any} geoJson - The GeoJSON object to be checked.
 * @returns {boolean} True if the GeoJSON object contains at least one feature with non-empty geometry coordinates, otherwise false.
 */
export const geoJsonHasGeometries = (geoJson: any): boolean => {
  const featuresWithGeometries = geoJson.features.filter(
    (feature: any) =>
      feature.geometry.coordinates && feature.geometry.coordinates.length > 0
  );
  return featuresWithGeometries.length > 0;
};

/**
 * Filters the features of a given GeoJSON FeatureCollection based on a specified group key.
 *
 * @param {FeatureCollection} geoJSON - The GeoJSON FeatureCollection to be filtered.
 * @param {string} key - The group key used to filter features. If the key is "all" or is not provided, no filtering is applied.
 * @returns {FeatureCollection} - A new GeoJSON FeatureCollection containing only the features that match the specified group key.
 */
export const filterGeoJsonFeaturesByGroup = (
  geoJSON: FeatureCollection,
  key: string
): FeatureCollection => {
  if (!key || key === 'all') return geoJSON;
  const newGeoJson = { ...geoJSON };
  newGeoJson.features = [...geoJSON.features].filter(
    (feature: any) => feature.properties.group === key
  );
  return newGeoJson;
};

/**
 * Retrieves the districts from the provided geoData selection.
 *
 * @param {function(): (GeoData|null)} geoData - A function that returns GeoData or null.
 * The GeoData object should contain a geoJson property if available.
 * @returns {ComputedRef<(Array|Null)>} A computed reference to an array of districts
 * if the geoJson property exists in the GeoData, otherwise null.
 */
export const getDistrictsFromSelection = (geoData: () => GeoData | null) => {
  return computed(() => {
    const data = geoData();
    if (data && data.geoJson) {
      return getDistrictsFromGeoData(data);
    } else {
      return null;
    }
  });
};

/**
 * Extracts district information from geographical data.
 *
 * @param {GeoData} geoData - The geographical data object containing geoJson data.
 * @param {string} [groupKey] - Optional key used to filter features within the geoJson data.
 * @returns {KeyValueDistricts|null} A dictionary of districts where each district contains key, name, and other properties extracted from the geoJson data, or null if the geoData or geoJson is invalid.
 */
export const getDistrictsFromGeoData = (
  geoData: GeoData,
  groupKey?: string
): KeyValueDistricts | null => {
  if (!geoData || !geoData.geoJson) return null;
  let featureCollection = JSON.parse(geoData.geoJson) as FeatureCollection;
  if (groupKey) {
    featureCollection = filterGeoJsonFeaturesByGroup(
      featureCollection,
      groupKey
    );
  }
  const districts: KeyValueDistricts = {};
  featureCollection.features.forEach(feature => {
    if (
      feature.properties &&
      feature.properties['key'] &&
      feature.properties['name']
    ) {
      const key = String(feature.properties['key']);
      districts[key] = {
        ...feature.properties,
        key: key,
        name: feature.properties['name'],
      };
    }
  });

  return districts;
};

/**
 * Extracts unique group names from a collection of districts.
 *
 * @param {KeyValueDistricts} districts - An object where the keys represent district identifiers,
 * and the values are district objects with a 'group' property.
 * @returns {string[]} An array of unique group names extracted from the districts.
 */
export const getGroupsFromDistricts = (
  districts: KeyValueDistricts
): string[] => {
  return makeUniqueStringArray(
    Object.values(districts)
      .map(district => district.group || '')
      .filter(Boolean)
  );
};
